// Header nav redesign (2026): desktop dropdown panels + desktop search panel.
// Loaded by sections/header.liquid only when "Desktop menu type" is "Dropdown panels".

(() => {
  const HOVER_QUERY = window.matchMedia('(hover: hover) and (pointer: fine)');
  const OPEN_DELAY = 80;
  const CLOSE_DELAY = 180;

  class HeaderNav extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-nav-item]'));
      this.openItem = null;

      this.items.forEach((item) => {
        const trigger = item.querySelector('[data-nav-trigger]');
        if (!trigger) return;

        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          window.clearTimeout(this.openTimer);
          // With a mouse the panel is already open from hover, so a click shouldn't
          // snap it shut. Keyboard "clicks" (detail === 0) and touch still toggle.
          const pointerClick = event.detail > 0 && HOVER_QUERY.matches;
          if (pointerClick || !this.isOpen(item)) {
            this.open(item);
          } else {
            this.close(item);
          }
        });

        item.addEventListener('mouseenter', () => {
          if (!HOVER_QUERY.matches) return;
          window.clearTimeout(this.closeTimer);
          window.clearTimeout(this.openTimer);
          this.openTimer = window.setTimeout(() => this.open(item), this.openItem ? 0 : OPEN_DELAY);
        });

        item.addEventListener('mouseleave', () => {
          if (!HOVER_QUERY.matches) return;
          window.clearTimeout(this.openTimer);
          this.closeTimer = window.setTimeout(() => this.close(item), CLOSE_DELAY);
        });
      });

      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);

      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('header-search-panel:open', () => this.closeAll());

      // Close when the sticky header hides itself on scroll
      this.sectionHeader = this.closest('.section-header');
      if (this.sectionHeader) {
        this.headerObserver = new MutationObserver(() => {
          if (this.sectionHeader.classList.contains('shopify-section-header-hidden')) this.closeAll();
        });
        this.headerObserver.observe(this.sectionHeader, { attributes: true, attributeFilter: ['class'] });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      this.headerObserver?.disconnect();
    }

    isOpen(item) {
      return item.classList.contains('is-open');
    }

    open(item) {
      if (this.openItem && this.openItem !== item) this.close(this.openItem);
      item.classList.add('is-open');
      item.querySelector('[data-nav-trigger]')?.setAttribute('aria-expanded', 'true');
      this.openItem = item;
      this.sectionHeader?.classList.add('header-nav-open');
      document.dispatchEvent(new CustomEvent('header-nav:open'));
    }

    close(item) {
      item.classList.remove('is-open');
      item.querySelector('[data-nav-trigger]')?.setAttribute('aria-expanded', 'false');
      if (this.openItem === item) {
        this.openItem = null;
        this.sectionHeader?.classList.remove('header-nav-open');
      }
    }

    closeAll() {
      this.items.forEach((item) => this.close(item));
    }

    onKeydown(event) {
      if (event.key !== 'Escape' || !this.openItem) return;
      const trigger = this.openItem.querySelector('[data-nav-trigger]');
      this.close(this.openItem);
      trigger?.focus();
    }

    onDocumentClick(event) {
      if (this.openItem && !this.openItem.contains(event.target)) this.close(this.openItem);
    }

    onFocusOut(event) {
      if (this.openItem && !this.openItem.contains(event.relatedTarget)) this.close(this.openItem);
    }
  }

  class HeaderSearchPanel extends HTMLElement {
    connectedCallback() {
      this.toggles = Array.from(document.querySelectorAll(`[aria-controls="${this.id}"][data-search-panel-toggle]`));
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearch = this.querySelector('predictive-search');
      this.desktopQuery = window.matchMedia('(min-width: 1025px)');

      this.toggles.forEach((toggle) =>
        toggle.addEventListener('click', (event) => {
          event.preventDefault();
          this.isOpen() ? this.close() : this.open();
        }),
      );

      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onBreakpointChange = () => {
        if (!this.desktopQuery.matches) this.close({ restoreFocus: false });
      };

      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('header-nav:open', () => this.close({ restoreFocus: false }));
      this.desktopQuery.addEventListener('change', this.onBreakpointChange);

      // The widget's own close (X) button. predictive-search.js calls stopPropagation()
      // on that click, so listen in the capture phase to still hear it.
      this.addEventListener(
        'click',
        (event) => {
          if (event.target.closest('.close-drawer-btn')) {
            window.setTimeout(() => this.close({ restoreFocus: false }), 0);
          }
        },
        true,
      );
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
    }

    isOpen() {
      return this.classList.contains('is-open');
    }

    open() {
      document.dispatchEvent(new CustomEvent('header-search-panel:open'));
      this.classList.add('is-open');
      this.toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'true'));
      // Focus on the next frame: the input's focus handler opens the predictive-search
      // widget, and doing it inside this click would let the widget's own
      // outside-click handler close it again straight away.
      window.requestAnimationFrame(() => this.input?.focus());
    }

    close({ restoreFocus = true } = {}) {
      if (!this.isOpen()) return;
      const hadFocus = this.contains(document.activeElement);
      if (this.predictiveSearch?.isOpen) this.predictiveSearch.close();
      this.classList.remove('is-open');
      this.toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
      if (restoreFocus && hadFocus) this.toggles[0]?.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen()) this.close();
    }

    onDocumentClick(event) {
      if (!this.isOpen()) return;
      if (this.contains(event.target)) return;
      if (this.toggles.some((toggle) => toggle.contains(event.target))) return;
      this.close({ restoreFocus: false });
    }
  }

  // Mobile drawer accordions (header-drawer.liquid, 2026 branch), e.g. Shop > Totes & Bags.
  // MenuDrawer (global.js) treats every <button> in the drawer as a "back" button, so catch
  // these clicks in the capture phase and stop them before they reach its handler.
  document.addEventListener(
    'click',
    (event) => {
      const toggle = event.target.closest('[data-nav26-accordion]');
      if (!toggle) return;
      event.stopPropagation();
      event.preventDefault();
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      if (panel) panel.hidden = expanded;
    },
    true,
  );

  if (!customElements.get('header-nav')) customElements.define('header-nav', HeaderNav);
  if (!customElements.get('header-search-panel')) customElements.define('header-search-panel', HeaderSearchPanel);
})();
